import { SimpleCache } from './simple-cache';
export const safeUrlRegex = /(javascript|about|data)(:|%3A)/gi;
const domainRegex = /^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^:/?\n]+)/gim;
const urlRegex = /[(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/i;
export function getUrlParameters(url = '') {
    const urlParams = {};
    const cache = SimpleCache.get(url);
    if (cache) {
        return cache;
    }
    if (url && url.indexOf('?') > -1) {
        // For modern browsers
        try {
            new URL(url).searchParams.forEach((value, key) => {
                urlParams[key] = value;
            });
        }
        catch (_a) {
            url = url.substring(url.indexOf('?') + 1);
            let match;
            // Regex for replacing addition symbol with a space
            const search = /([^&=]+)=?([^&]*)/g;
            const decode = (s) => {
                try {
                    s = decodeURIComponent(s.replace(/\+/g, ' '));
                    // eslint-disable-next-line no-empty
                }
                catch (_a) { }
                return s;
            };
            const query = url || window.location.search.substring(1);
            while ((match = search.exec(query))) {
                urlParams[decode(match[1])] = decode(match[2]);
            }
        }
    }
    SimpleCache.set(url, urlParams);
    return urlParams;
}
export function isEncodedUrl(url) {
    // What to do with app links like blocket://
    return (url
        .toLowerCase()
        .replace(/(https?%3A)?(?=%2F%2F)/i, '')
        .indexOf('%2f%2f') === 0);
}
export function isRelativeUrl(url) {
    return !!url && url.replace(/^https?:/, '').indexOf('//') !== 0;
}
export function isUrl(url) {
    return typeof url === 'string' && !!url.match(urlRegex);
}
export function isBase64Image(str) {
    return !!str && str.startsWith('data:image');
}
export function isBase64Mp4Video(str) {
    return !!str && str.startsWith('data:video/mp4;base64');
}
export function isBase64SVG(str) {
    return !!str && str.startsWith('data:image/svg+xml;base64');
}
export function addProtocol(url, protocol = 'https') {
    if (!url || url === '#') {
        return url;
    }
    // Links without protocol messes up iOS safari
    if (url.indexOf('//') === 0) {
        url = `${protocol}:${url}`;
    }
    // Make sure url starts with http:// or https://
    else if (url.indexOf('http') !== 0) {
        url = `${protocol}://${url}`;
    }
    return url;
}
/**
 * Merge two parts of a url.
 * Applies a slash between if missing and removes if it has two adjacent.
 * @param domain
 * @param path
 */
export function concatUrl(domain = '', path = '') {
    if (domain.length && path.length) {
        if (domain.charAt(domain.length - 1) !== '/') {
            domain += '/';
        }
        if (path.charAt(0) === '/') {
            path = path.substr(1);
        }
    }
    return domain + path;
}
export function getDomain(url, stripProtocol = true) {
    if (typeof url === 'string') {
        const match = url.match(domainRegex);
        if (match) {
            return stripProtocol ? removeProtocol(match[0]) : match[0];
        }
    }
}
export function getDomainFromURL(url) {
    return new URL(url).hostname;
}
export function removeProtocol(url) {
    return url.replace(/^((https?:)?\/\/)/gim, '');
}
export function hasParameter(parameters, key) {
    return parameters[key] !== undefined;
}
export function parameterToBool(parameter) {
    if (!parameter) {
        return undefined;
    }
    switch (parameter.toLowerCase()) {
        case '1':
        case 'on':
        case 'true':
        case 'yes':
            return true;
        case '0':
        case 'off':
        case 'false':
        case 'no':
            return false;
        default:
            return undefined;
    }
}
export function parameterToUrl(parameter) {
    if (!parameter) {
        return '';
    }
    return isEncodedUrl(parameter) ? decodeURIComponent(parameter) : parameter;
}
export function replaceOrigin(url, newOrigin) {
    if (!newOrigin) {
        return url;
    }
    return url.replace(new URL(url).origin, new URL(newOrigin).origin);
}
export function isBannerflow(url) {
    if (!url) {
        return false;
    }
    try {
        const hostname = typeof URL === 'function' ? new URL(url).hostname : getDomain(url, true);
        return !!hostname && !!hostname.match(/bannerflow./g);
    }
    catch (_a) {
        return false;
    }
}
export function isSameDomain(a, b) {
    return getDomain(a) === getDomain(b);
}
export function removeQueryParams(url) {
    return url.replace(/\?.*/g, '');
}
export function getUrlsInString(str) {
    return (str === null || str === void 0 ? void 0 : str.match(/(https?:\/\/[^\s]+)/g)) || [];
}
/**
 * Return the first url that's not undefined. '' means that we should prevent click
 */
export function getFirstUrl(...urls) {
    // NOSONAR
    for (let i = 0; i < urls.length; i++) {
        if (urls[i] !== undefined && urls[i] !== null) {
            return urls[i];
        }
    }
    return undefined;
}
export function objectToQueryString(params, removeEmpty = true) {
    if (typeof params === 'object') {
        const queries = [];
        for (const key in params) {
            const value = params[key];
            if ((value !== undefined && value.toString()) || !removeEmpty) {
                queries.push(`${key}=${value}`);
            }
        }
        return queries.length ? `?${queries.join('&')}` : '';
    }
    return '';
}
export function getFileExtension(url) {
    var _a;
    if (!url) {
        return undefined;
    }
    const split = url.split(/[#?]/);
    const fragment = split[0];
    const dotChunks = fragment.split('.');
    if (dotChunks.length === 1) {
        return undefined; // no extension found
    }
    const extension = (_a = dotChunks.pop()) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase();
    if (!extension) {
        return undefined;
    }
    const containsALetter = /[a-zA-Z]/g.test(extension);
    if (!containsALetter) {
        return undefined;
    }
    // True if it only contains 1 or more alphanumeric characters (symbols or spaces)
    const containsOnlyLettersAndNumbers = /^[a-zA-Z0-9]+$/gi.test(extension);
    return containsOnlyLettersAndNumbers ? extension : undefined;
}
export function removeFileExtension(value) {
    return (value || '').replace(/\.(jpg|png|gif|svg|mp4|psd)$/i, '');
}
export function isVideoFileUrl(url) {
    const ext = getFileExtension(url);
    if (ext) {
        // Check for the whole string being "mp4", "mov", "webm" or "h264"
        return /^(mp4|mov|webm|h264)$/.test(ext);
    }
    return false;
}
export function isSVGFile(url) {
    if (isBase64SVG(url)) {
        return true;
    }
    const ext = getFileExtension(url);
    return ext === 'svg';
}
export function isGIFFile(url) {
    const ext = getFileExtension(url);
    return ext === 'gif';
}
export function isInLocalEnvironment() {
    if (!window) {
        return false;
    }
    if (window.location.protocol === 'file:') {
        return true;
    }
    return false;
}
export function getOrigin(str) {
    try {
        const url = new URL(str);
        return url.origin;
    }
    catch (_a) {
        try {
            if (!isUrl(str)) {
                throw new Error('Provided value is not a valid url.');
            }
            const a = document.createElement('a');
            a.href = str;
            return a.origin;
        }
        catch (e) {
            throw new Error(e);
        }
    }
}
export function getFileName(url) {
    const segments = new URL(url).pathname.split('/');
    return segments.pop() || url;
}
export function getFileLocation(url) {
    const segments = url.split('/');
    segments.pop();
    return segments.join('/');
}
export function addMissingProtocol(url) {
    return /https?:\/\//.test(url) ? url : `https://${url}`;
}
export function extractURL(str) {
    const url = /http[s]?:\/\/[^\s]+/i;
    const match = str.match(url);
    return match ? match[0] : undefined;
}
